import type { UserDerivedKey } from "deso-protocol";
import { getAppState } from "deso-protocol";
import { identity } from "deso-protocol";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  authorizeDerivedKeyAndWait,
  createDerivedKey,
  DerivedKeyType,
  getDerivedKey,
} from "@/backend/focus";
import { createToastErrorDetails } from "@/components/ui/toast-helpers";
import { useToast } from "@/components/ui/use-toast";
import { integerToNanos } from "@/utils/currency";
import {
  DERIVED_KEY_BLOCK_HEIGHT_OFFSET,
  needsDerivedKeyAuthorizationRefresh,
} from "@/utils/derived-key";

type ApprovedDerivedKeyProps = {
  creatorPublicKey: string;
  derivedPublicKey: string;
  limit: number;
};

const approveDerivedKey = async ({
  creatorPublicKey,
  derivedPublicKey,
  limit,
}: ApprovedDerivedKeyProps) => {
  if (!process.env.NEXT_PUBLIC_FOCUS_APP_PUBLIC_KEY) {
    throw new Error("Focus app public key is not set.");
  }

  return identity.derive(
    {
      GlobalDESOLimit: integerToNanos(limit),
      TransactionCountLimitMap: {
        AUTHORIZE_DERIVED_KEY: 1,
        CREATE_POST_ASSOCIATION: "UNLIMITED",
        DELETE_POST_ASSOCIATION: "UNLIMITED",
        CREATE_USER_ASSOCIATION: "UNLIMITED",
        DELETE_USER_ASSOCIATION: "UNLIMITED",
        BASIC_TRANSFER: "UNLIMITED",
        DAO_COIN_TRANSFER: "UNLIMITED",
        NEW_MESSAGE: "UNLIMITED",
        DAO_COIN: "UNLIMITED",
      },
      DAOCoinOperationLimitMap: {
        [process.env.NEXT_PUBLIC_DUSD_PUBLIC_KEY]: {
          transfer: "UNLIMITED",
        },
        [process.env.NEXT_PUBLIC_FOCUS_APP_PUBLIC_KEY]: {
          transfer: "UNLIMITED",
        },
      },
    },
    {
      ownerPublicKey: creatorPublicKey,
      derivedPublicKey: derivedPublicKey,
      expirationDays: 365 * 100, // 100 years, effectively infinite.
    },
  );
};

export enum UseDerivedKeyStatus {
  EMPTY,
  IDLE,
  CREATING,
  AUTHORIZING,
  INVALID,
}

type DerivedKeyState = {
  key: string;
  ownerPublicKey: string;
};

type UseDerivedKeyProps = {
  publicKey?: string | null;
  type?: DerivedKeyType;
};

export type UseDerivedKeyReturnValue = ReturnType<typeof useDerivedKey>;

export const useDerivedKey = ({
  publicKey,
  type = DerivedKeyType.GENERAL,
}: UseDerivedKeyProps) => {
  const [status, setStatus] = useState<UseDerivedKeyStatus>(
    UseDerivedKeyStatus.EMPTY,
  );
  const derivedKeyStateRef = useRef<DerivedKeyState | null>(null);
  const createStateRef = useRef<{
    ownerPublicKey: string;
    promise: Promise<DerivedKeyState>;
  } | null>(null);
  const latestPublicKeyRef = useRef(publicKey);
  const { toast } = useToast();

  latestPublicKeyRef.current = publicKey;

  // Temporarily leaving this here for production debugging in case we still have issues
  // We can ask the user to run this function and show what data he's getting
  if (type === DerivedKeyType.GENERAL && typeof window !== "undefined") {
    // @ts-expect-error it's fine
    window._getDerivedKey ??= async () => {
      console.log(
        await createDerivedKey({
          // @ts-expect-error it's fine
          OwnerPublicKey: publicKey,
          DerivedKeyType: type,
        }).catch((e) => e),
        await getDerivedKey({
          derivedKeyType: type,
          // @ts-expect-error it's fine
          ownerPublicKey: publicKey,
        }).catch((e) => e),
        await fetch(
          // @ts-expect-error it's fine
          `${process.env.NEXT_PUBLIC_CORE_DESO_NODE}/api/v0/get-single-derived-key/${publicKey}/${derivedKeyStateRef.current.key}`,
        )
          .then((d) => d.json())
          .catch((e) => e),
      );
    };
  }

  useEffect(() => {
    derivedKeyStateRef.current = null;
    createStateRef.current = null;
    setStatus(UseDerivedKeyStatus.EMPTY);
  }, [publicKey, type]);

  const create = useCallback(
    async ({ publicKey: pk = publicKey }) => {
      if (!pk) return;

      if (derivedKeyStateRef.current?.ownerPublicKey === pk) {
        return derivedKeyStateRef.current;
      }

      if (createStateRef.current?.ownerPublicKey === pk) {
        return createStateRef.current.promise;
      }

      setStatus(UseDerivedKeyStatus.CREATING);

      const createPromise = createDerivedKey({
        OwnerPublicKey: pk,
        DerivedKeyType: type,
      }).then(({ DerivedPublicKeyBase58Check }) => ({
        key: DerivedPublicKeyBase58Check,
        ownerPublicKey: pk,
      }));

      createStateRef.current = {
        ownerPublicKey: pk,
        promise: createPromise,
      };

      try {
        const state = await createPromise;

        if (createStateRef.current?.promise === createPromise) {
          createStateRef.current = null;
        }

        if (latestPublicKeyRef.current === pk) {
          derivedKeyStateRef.current = state;
          setStatus(UseDerivedKeyStatus.IDLE);
        }

        return state;
      } catch (error) {
        if (createStateRef.current?.promise === createPromise) {
          createStateRef.current = null;
        }

        if (latestPublicKeyRef.current === pk) {
          setStatus(UseDerivedKeyStatus.EMPTY);
        }

        throw error;
      }
    },
    [type, publicKey],
  );

  const authorize = useCallback(
    async ({
      desoLimit = 0.1,
      publicKey: pk = publicKey,
      spendAmountDesoNanos = 0,
    }: {
      desoLimit?: number;
      publicKey?: string | null;
      spendAmountDesoNanos?: number;
    } = {}) => {
      if (!pk) {
        return {
          success: false,
          error: new Error("No public key"),
        };
      }

      if (derivedKeyStateRef.current?.ownerPublicKey !== pk) {
        const createdState = await create({ publicKey: pk });

        if (!createdState) {
          return {
            success: false,
            error: new Error("Failed to create derived key"),
          };
        }
      }

      setStatus(UseDerivedKeyStatus.AUTHORIZING);

      try {
        const keyData: UserDerivedKey = (await fetch(
          `${process.env.NEXT_PUBLIC_CORE_DESO_NODE}/api/v0/get-single-derived-key/${pk}/${derivedKeyStateRef.current!.key}`,
        )
          .then((res) => res.json())
          .then((data) => data.DerivedKey)
          .catch(() => ({
            IsValid: false,
          }))) || { IsValid: false };

        const appState = await getAppState();

        if (
          needsDerivedKeyAuthorizationRefresh({
            blockHeight: appState.BlockHeight,
            blockHeightOffset: DERIVED_KEY_BLOCK_HEIGHT_OFFSET,
            derivedKey: keyData,
            spendAmountDesoNanos,
          })
        ) {
          const identityResponse = await approveDerivedKey({
            creatorPublicKey: pk,
            derivedPublicKey: derivedKeyStateRef.current!.key,
            limit: desoLimit,
          });

          await authorizeDerivedKeyAndWait({
            OwnerPublicKey: pk,
            DerivedPublicKey: derivedKeyStateRef.current!.key,
            ExpirationBlock: identityResponse.expirationBlock,
            AccessSignature: identityResponse.accessSignature,
            TransactionSpendingLimitHex:
              identityResponse.transactionSpendingLimitHex,
          });
        }

        setStatus(UseDerivedKeyStatus.IDLE);

        return { success: true, error: null };
      } catch (e) {
        setStatus(UseDerivedKeyStatus.IDLE);
        toast({
          variant: "destructive",
          title: "Failed to authorize derived key",
          description: "The derived key authorization request failed.",
          details: createToastErrorDetails(e),
        });
        return { success: false, error: e };
      }
    },
    [create, publicKey, toast],
  );

  const clear = useCallback(() => {
    derivedKeyStateRef.current = null;
    setStatus(UseDerivedKeyStatus.EMPTY);
  }, []);

  return {
    status,
    derivedKey: derivedKeyStateRef.current?.key,
    create,
    authorize,
    clear,
  };
};
